import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "";

const CORRELATOR_META = {
  queue_delay: {
    label: "Queue Delay Risk",
    color: "#E24B4A",
    formula: "MIN(100, withdrawal_rate \u00d7 1.45)",
    inputs: ["total_projects", "withdrawn_count", "withdrawal_rate"],
    description: "LBNL interconnection queue withdrawal rate \u2192 slip risk 0\u2013100"
  },
  site_risk: {
    label: "Site Risk",
    color: "#BA7517",
    formula: "(climate \u00d7 0.35) + (capacity \u00d7 0.35) + (labor \u00d7 0.30)",
    inputs: ["climate_score", "capacity_score", "labor_score"],
    description: "NOAA + EIA + BLS composite \u2192 site risk 0\u2013100"
  },
  procurement_risk: {
    label: "Procurement Risk",
    color: "#8B5CF6",
    formula: "(capacity_trend \u00d7 0.50) + (permit_backlog / 500 \u00d7 0.50)",
    inputs: ["capacity_trend", "permit_backlog"],
    description: "EIA trend + perf_gov permit backlog \u2192 lead-time risk 0\u2013100"
  }
};

const scoreColor = (v) => {
  if (v === null || v === undefined) return "#6B7280";
  if (v >= 75) return "#E24B4A";
  if (v >= 50) return "#BA7517";
  if (v >= 25) return "#1D9E75";
  return "#185FA5";
};

const riskLabel = (v) => {
  if (v === null || v === undefined) return "\u2014";
  if (v >= 75) return "EXTREME";
  if (v >= 50) return "HIGH";
  if (v >= 25) return "MODERATE";
  return "LOW";
};

export default function IntelligenceAudit() {
  const [sites, setSites] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/intelligence/audit`)
      .then(r => r.json())
      .then(d => { setSites(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 48, color: "#9a99a2" }}>Loading audit data...</div>;

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#e2e0d8",
                  fontFamily: "'Inter', sans-serif", padding: "24px 32px" }}>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "#5f5e6e", letterSpacing: 2,
                      textTransform: "uppercase", marginBottom: 4 }}>
          Intelligence Data
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#e8e6de" }}>
          Computation Audit
        </h1>
        <div style={{ fontSize: 12, color: "#5f5e6e", marginTop: 4 }}>
          CORR-004 score derivation — raw inputs · formula · computed result · per site
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {sites.map(site => {
          const isExpanded = expanded === site.edge_node_id;
          const qd = site.correlators?.queue_delay;
          const sr = site.correlators?.site_risk;
          const pr = site.correlators?.procurement_risk;

          return (
            <div key={site.edge_node_id}
              style={{
                background: isExpanded ? "#1a1a2e" : "#12121f",
                border: `1px solid ${isExpanded ? "#185FA5" : "#2a2a3e"}`,
                borderRadius: 8, overflow: "hidden",
              }}
            >
              {/* Site header */}
              <div
                onClick={() => setExpanded(isExpanded ? null : site.edge_node_id)}
                style={{
                  padding: "16px 20px", cursor: "pointer",
                  display: "flex", justifyContent: "space-between", alignItems: "center"
                }}
              >
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#e8e6de" }}>
                    {site.site_name}
                  </div>
                  <div style={{ fontSize: 12, color: "#5f5e6e", marginTop: 2 }}>
                    {site.geo_state} · {site.iso_region} · {site.edge_node_id}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  {[
                    { label: "Queue", value: qd?.score, meta: CORRELATOR_META.queue_delay },
                    { label: "Site", value: sr?.score, meta: CORRELATOR_META.site_risk },
                    { label: "Procurement", value: pr?.score, meta: CORRELATOR_META.procurement_risk },
                  ].map(({ label, value, meta }) => (
                    <div key={label} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: scoreColor(value) }}>
                        {value != null ? Math.round(value) : "\u2014"}
                      </div>
                      <div style={{ fontSize: 10, color: "#5f5e6e" }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expanded correlator panels */}
              {isExpanded && (
                <div style={{ borderTop: "1px solid #2a2a3e", padding: "16px 20px",
                              display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                  {Object.entries(CORRELATOR_META).map(([key, meta]) => {
                    const corr = site.correlators?.[key];
                    const inputs = corr?.meta || {};

                    return (
                      <div key={key} style={{
                        background: "#0d0d1a",
                        border: `1px solid ${meta.color}33`,
                        borderTop: `3px solid ${meta.color}`,
                        borderRadius: 6, padding: 14
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: meta.color, marginBottom: 4 }}>
                          {meta.label}
                        </div>
                        <div style={{ fontSize: 11, color: "#5f5e6e", marginBottom: 10 }}>
                          {meta.description}
                        </div>

                        {/* Score */}
                        <div style={{ display: "flex", justifyContent: "space-between",
                                      alignItems: "center", marginBottom: 10 }}>
                          <span style={{ fontSize: 28, fontWeight: 700,
                                         color: scoreColor(corr?.score) }}>
                            {corr?.score != null ? Math.round(corr.score) : "\u2014"}
                          </span>
                          <span style={{
                            fontSize: 10, color: scoreColor(corr?.score),
                            background: scoreColor(corr?.score) + "22",
                            padding: "3px 8px", borderRadius: 10
                          }}>
                            {riskLabel(corr?.score)}
                          </span>
                        </div>

                        {/* Formula */}
                        <div style={{
                          fontSize: 10, color: meta.color, fontFamily: "monospace",
                          background: meta.color + "11", padding: "4px 8px",
                          borderRadius: 4, marginBottom: 10
                        }}>
                          {meta.formula}
                        </div>

                        {/* Raw inputs */}
                        <div style={{ fontSize: 11, color: "#5f5e6e", marginBottom: 4 }}>
                          Raw inputs:
                        </div>
                        {Object.entries(inputs).length > 0 ? (
                          <>
                            {/* Weighted breakdown for site_risk if normalized scores present */}
                            {key === "site_risk" && inputs.climate_score_normalized != null && (() => {
                              const rows = [
                                { label: "climate", val: inputs.climate_score_normalized, weighted: inputs.climate_weighted, w: 0.35 },
                                { label: "capacity", val: inputs.capacity_score_normalized, weighted: inputs.capacity_weighted, w: 0.35 },
                                { label: "labor", val: inputs.labor_score_normalized, weighted: inputs.labor_weighted, w: 0.30 },
                              ];
                              const total = rows.reduce((s, r) => s + (r.weighted ?? r.val * r.w), 0);
                              return (
                                <div style={{ marginBottom: 8, padding: "6px 8px",
                                              background: meta.color + "08", borderRadius: 4 }}>
                                  {rows.map(({ label, val, weighted, w }) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between",
                                                              fontSize: 11, marginBottom: 2 }}>
                                      <span style={{ color: "#9a99a2" }}>
                                        {label}: {val != null ? val.toFixed(1) : "\u2014"} \u00d7 {w}
                                      </span>
                                      <span style={{ color: meta.color, fontWeight: 600 }}>
                                        = {weighted != null ? weighted.toFixed(1) : (val != null ? (val * w).toFixed(1) : "\u2014")}
                                      </span>
                                    </div>
                                  ))}
                                  <div style={{ borderTop: "1px solid #2a2a3e", marginTop: 4, paddingTop: 4,
                                                display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                                    <span style={{ color: "#9a99a2" }}>total</span>
                                    <span style={{ color: meta.color, fontWeight: 700 }}>
                                      = {total.toFixed(1)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                            {Object.entries(inputs)
                              .filter(([k]) => !k.endsWith("_normalized") && !k.endsWith("_weighted"))
                              .map(([k, v]) => (
                              <div key={k} style={{ display: "flex", justifyContent: "space-between",
                                                    fontSize: 11, marginBottom: 2 }}>
                                <span style={{ color: "#5f5e6e" }}>{k.replace(/_/g, " ")}</span>
                                <span style={{ color: "#e2e0d8" }}>
                                  {typeof v === "number"
                                    ? (Number.isInteger(v) ? v.toLocaleString() : v.toFixed(4))
                                    : typeof v === "boolean" ? (v ? "yes" : "no")
                                    : String(v)}
                                </span>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div style={{ fontSize: 11, color: "#5f5e6e" }}>No input metadata stored</div>
                        )}

                        {/* Confidence + timestamp */}
                        <div style={{ marginTop: 10, paddingTop: 8,
                                      borderTop: "1px solid #2a2a3e", fontSize: 10, color: "#5f5e6e" }}>
                          Confidence: {corr?.confidence || "\u2014"} ·{" "}
                          {corr?.computed_at ? new Date(corr.computed_at).toLocaleString() : "\u2014"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
